import { useCallback, useMemo } from 'react';

import { ClearFiltersButton } from '@/components/base/clear-filters-button';
import { Select } from '@/components/ui/select';
import { useTableContext } from '@/contexts/table-context';

import { useDashboardFilters } from './use-dashboard-filters';

type FiltersProps = {
  /** Called after clear so the parent can remount Search (local input state). */
  onCleared?: () => void;
};

export function Filters({ onCleared }: FiltersProps) {
  const {
    searchQuery,
    filters: appliedFilters,
    setFilters,
    sorting,
    resetQueryState,
  } = useTableContext();
  const filters = useDashboardFilters();

  const handleApply = (
    selectedValues: (string | boolean | undefined)[],
    key: string,
  ) => {
    setFilters(prev => ({
      ...prev,
      [key]: selectedValues,
    }));
  };

  const activeFilterCount = useMemo(() => {
    const multiSelectCount =
      appliedFilters.brandIds.length +
      appliedFilters.positionIds.length +
      appliedFilters.locationIds.length;
    const searchCount = searchQuery?.trim() ? 1 : 0;
    const sortCount = sorting.length > 0 ? 1 : 0;

    return multiSelectCount + searchCount + sortCount;
  }, [appliedFilters, searchQuery, sorting]);

  const handleClearAll = useCallback(() => {
    // One atomic reset — calling setFilters + setSearchQuery + setSorting in
    // the same handler races `setSearchParams` and leaves stale URL params.
    // Only reset filters this page surfaces — not openings-only keys
    // (`isSelectedForSourcing`, `active`).
    resetQueryState({
      filters: {
        ...appliedFilters,
        brandIds: [],
        positionIds: [],
        locationIds: [],
      },
      searchQuery: undefined,
      sorting: [],
    });
    onCleared?.();
  }, [resetQueryState, appliedFilters, onCleared]);

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {filters.map(filter => (
        <Select
          key={filter.label}
          id={filter.filterKey}
          options={filter.options ?? []}
          selectedValues={filter.selectedValues}
          prefixText={`${filter.label}:`}
          query={filter.query}
          setQuery={filter.setQuery}
          isLoading={filter.isFetching}
          onApply={handleApply}
          className="rounded-lg"
        />
      ))}
      <ClearFiltersButton count={activeFilterCount} onClick={handleClearAll} />
    </div>
  );
}
